import { FC, useContext } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";

import Card from "components/v2/Card/Card";
import EmptyState from "components/v2/EmptyState/EmptyState";
import { ReactComponent as AlertIcon } from "assets/images/iconsV2/alert-circle-grey.svg";
import { ReactComponent as EditIcon } from "assets/images/iconsV2/edit-02.svg";
import Badge from "components/v2/Badge/Badge";
import Button from "components/v2/Button/Button";
import { useGetShipmentBasic } from "api/queries/shipments/useGetShipmentBasic";
import useCustomBrokerPermission from "hooks/useCustomBrokerPermission";
import usePermissions from "hooks/usePermissions";
import { ShipmentTrackingContext } from "pages/common/ShipmentTracking/ShipmentTrackingContext";
import ScheduleInfo from "./components/ScheduleInfo/ScheduleInfo";
import ScheduleStopList from "./components/ScheduleStopList/ScheduleStopList";
import { FreightModeBaseTypes } from "../../../../../constants";
import { getHeaderTitle, OceanVesselLeg, VesselScheduleSummary } from "./scheduleUtils";

type ScheduleProps = {
  showErrorState: boolean;
  transitStops: any;
  isScheduleUpdated: boolean;
  freightModeType: typeof FreightModeBaseTypes[keyof typeof FreightModeBaseTypes];
  vesselScheduleSummary?: VesselScheduleSummary | null;
  oceanVesselLegs: OceanVesselLeg[];
};

const Schedule: FC<ScheduleProps> = function (props) {
  const { showErrorState, transitStops, isScheduleUpdated, freightModeType, vesselScheduleSummary, oceanVesselLegs } = props;
  const intl = useIntl();
  const { id: shipmentId } = useParams();
  const { permissions } = usePermissions();
  const [isCustomBroker] = useCustomBrokerPermission();
  const userType = JSON.parse(permissions ?? "{}").user_type;
  const { setIsEditScheduleDialogOpen } = useContext(ShipmentTrackingContext);

  const { data: shipmentData }: any = useGetShipmentBasic(shipmentId!, {
    enabled: !!shipmentId,
    refetchOnMount: false,
  });

  const handleEditScheduleClick = () => {
    setIsEditScheduleDialogOpen(true);
  };

  return (
    <>
      <Card.Header
        title={getHeaderTitle(freightModeType, intl)}
        supportingText={intl.formatMessage({
          id: "tracking.portcastAir.flightScheduleSubheader",
          defaultMessage: "Information here is updated by the provider",
        })}
        showBorder
        {...(isScheduleUpdated && {
          badge: (
            <Badge
              color="gray"
              label={intl.formatMessage({ id: "general.updated", defaultMessage: "Updated" })}
              type="status-badge"
            />
          ),
        })}
        actions={
          !["Canceled", "Auto canceled", "Delivered"].includes(shipmentData?.organization?.request_status) &&
          !isCustomBroker &&
          userType === "provider" ? (
            <Button
              label={intl.formatMessage({ id: "general.edit", defaultMessage: "Edit" })}
              variant="link-color"
              iconLeft={<EditIcon />}
              onClick={handleEditScheduleClick}
            />
          ) : null
        }
      />
      <Card.Body>
        {showErrorState ? (
          <EmptyState
            noActions
            customIcon={
              <div className="schedule-section__error-icon">
                <AlertIcon />
              </div>
            }
            title={intl.formatMessage({
              id: "general.couldNotLoadDetails",
              defaultMessage: "We couldn't load these details",
            })}
            description={intl.formatMessage({
              id: "general.tryAgainOrCheckBackLater",
              defaultMessage: "You can try again or check back later.",
            })}
          />
        ) : (
          <ScheduleInfo
            transitStops={transitStops}
            freightModeType={freightModeType}
            vesselScheduleSummary={vesselScheduleSummary}
            oceanVesselLegs={oceanVesselLegs}
          />
        )}
      </Card.Body>
      {!showErrorState && (
        <Card.Accordion
          accordionTitle={(isOpen) => isOpen
              ? intl.formatMessage({
                  id: "general.hideDetails",
                  defaultMessage: "Hide details",
                })
              : intl.formatMessage({
                  id: "general.showDetails",
                  defaultMessage: "Show details",
                })}
        >
          <ScheduleStopList
            transitStops={transitStops}
            freightModeType={freightModeType}
            oceanVesselLegs={oceanVesselLegs}
          />
        </Card.Accordion>
      )}
    </>
  );
};

export default Schedule;
